import React, { useState, useRef, useEffect } from "react";

// Import common components
import {
  MetricsLegend,
  ColorLegend,
  ScenarioSelector,
  ControlButton,
  NodeDetailsPopup,
  ChartsPopup,
  ExpandedChartModal
} from "../common";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Import map components
import SimpleMap from "./SimpleMap";

// Import context
import { useDataContext } from "../../context/DataContext";
import { SCORE_SCALE_WARN, SCORE_SCALE_WORST, losColor } from "../../../../constants/scoreScale";

// Import styles
import "../../styles/index.css";

// Import icon assets
import { ReactComponent as ConfrontoIcon } from "../../Assets/icons/confronto.svg";
import { ReactComponent as DownloadIcon } from "../../Assets/icons/download.svg";
import { ReactComponent as ChartsIcon } from "../../Assets/icons/charts.svg";
import { ReactComponent as XMarkIcon } from "../../Assets/icons/x-mark.svg";

// Import utilities
import { generateNodeReportPDF } from "../../utils/pdfExport";
import { FullscreenOutlined } from "@ant-design/icons";

// Helper function to get URL query parameters
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    scenario1: urlParams.get('scenario_1'),
    scenario2: urlParams.get('scenario_2'),
    node1: urlParams.get('node_1'),
    node2: urlParams.get('node_2')
  };
};

function Confronto() {

  // Track if URL params have been applied (to avoid re-applying on every render)
  const [urlParamsApplied, setUrlParamsApplied] = useState(false);

  // Get data from context
  const {
    dataJson,
    losDataJson,
    graficiDataJson,
    scenarioDescriptions,
    isLoading,
    dataLoaded,
    getSistemaMetrics,
    getDefaultMapCenter
  } = useDataContext();
  const [selectedScenario1, setSelectedScenario1] = useState(null);
  const [selectedScenario2, setSelectedScenario2] = useState(null);
  const [selectedIdMap1, setSelectedIdMap1] = useState(null);
  const [selectedIdMap2, setSelectedIdMap2] = useState(null);
  const [selectedRoadMap1, setSelectedRoadMap1] = useState(null);
  const [selectedRoadMap2, setSelectedRoadMap2] = useState(null);
  const [selectedRoadDataMap1, setSelectedRoadDataMap1] = useState(null);
  const [selectedRoadDataMap2, setSelectedRoadDataMap2] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(17);
  const [syncedZoom, setSyncedZoom] = useState(17); // Synchronized zoom for comparison mode
  const [zoomSourceMap, setZoomSourceMap] = useState(null); // Which map initiated the zoom (0 or 1)
  const [mapCenter, setMapCenter] = useState([45.640301546939185, 9.611915782993783]);
  const [nodeMetricsMap1, setNodeMetricsMap1] = useState({ flusso: 0, codaMedia: 0, ritardoMedio: 0 });
  const [nodeMetricsMap2, setNodeMetricsMap2] = useState({ flusso: 0, codaMedia: 0, ritardoMedio: 0 });
  const [showPopupMap1, setShowPopupMap1] = useState(false);
  const [showPopupMap2, setShowPopupMap2] = useState(false);
  const [nodeDataMap1, setNodeDataMap1] = useState(null);
  const [nodeDataMap2, setNodeDataMap2] = useState(null);
  const [nodeNameMap1, setNodeNameMap1] = useState(null);
  const [nodeNameMap2, setNodeNameMap2] = useState(null);
  const [showChartsPopup, setShowChartsPopup] = useState(false);
  const [currentChartScenario, setCurrentChartScenario] = useState(1);
  const [chartsPopupResetKey, setChartsPopupResetKey] = useState(0);
  const [chartsScrollTop, setChartsScrollTop] = useState(0);
  const [sharedExpandedChart, setSharedExpandedChart] = useState(null);
  const [singleExpandedChart, setSingleExpandedChart] = useState(null); // For single mode expanded charts
  const [chartDataMap1, setChartDataMap1] = useState(null);
  const [chartDataMap2, setChartDataMap2] = useState(null);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadDropdownRef = useRef(null);
  const [sharedTimeInterval, setSharedTimeInterval] = useState('10min'); // Synchronized time interval for comparison mode
  const [sharedSelectedRoad, setSharedSelectedRoad] = useState(''); // Synchronized selected road for comparison mode

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    if (showDownloadDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadDropdown]);

  // Shared tooltip state for synchronized tooltips in confronto mode
  // Track which street/section is selected by its ID
  const [sharedTooltipState, setSharedTooltipState] = useState({
    selectedSectionId: null, // Track the section/street ID
    showTooltip: false
  });

  // Update map center when data is loaded from context
  useEffect(() => {
    if (dataLoaded) {
      const defaultCenter = getDefaultMapCenter();
      setMapCenter(defaultCenter);
    }
  }, [dataLoaded, getDefaultMapCenter]);

  // Apply URL query parameters when data is loaded
  useEffect(() => {
    if (!dataLoaded || urlParamsApplied || !dataJson?.scenario) return;

    const { scenario1, scenario2, node1, node2 } = getUrlParams();

    console.log('URL Params:', { scenario1, scenario2, node1, node2 });
    console.log('Available scenarios:', dataJson.scenario.map(s => ({ id: s.replicaAvg, name: s.scName })));

    // If no URL params, skip
    if (!scenario1 && !scenario2) {
      setUrlParamsApplied(true);
      return;
    }

    // Find scenarios by name (scName) or replicaAvg ID
    // Search in both dataJson (grafi) and losDataJson (los) since they have different names
    const findScenarioByName = (name) => {
      if (!name) return null;
      const decodedName = decodeURIComponent(name);

      // Helper to check if a scenario matches
      const matchesScenario = (s) => {
        const scenarioName = s.scName || s.name || '';
        return (
          scenarioName === decodedName ||
          scenarioName.toLowerCase() === decodedName.toLowerCase() ||
          String(s.replicaAvg) === decodedName
        );
      };

      // First try to find in dataJson (grafi_per_scenario.json)
      let scenario = dataJson.scenario.find(matchesScenario);

      // If not found, try losDataJson (los_per_scenario.json) which has different names
      if (!scenario && losDataJson?.scenario) {
        const losScenario = losDataJson.scenario.find(matchesScenario);
        if (losScenario) {
          // Found in losDataJson, now verify this ID exists in dataJson
          scenario = dataJson.scenario.find(s => s.replicaAvg === losScenario.replicaAvg);
        }
      }

      console.log(`Finding scenario for "${decodedName}":`, scenario?.replicaAvg || 'NOT FOUND');
      return scenario?.replicaAvg || null;
    };

    const scenarioId1 = findScenarioByName(scenario1);
    const scenarioId2 = findScenarioByName(scenario2);

    console.log('Resolved scenario IDs:', { scenarioId1, scenarioId2 });

    // Apply all state changes together
    // First enable comparison mode if we have 2 scenarios
    if (scenarioId1 && scenarioId2) {
      console.log('Enabling comparison mode with both scenarios');
      setShowComparison(true);
      setSelectedScenario1(scenarioId1);
      setSelectedScenario2(scenarioId2);

      // Apply nodes if provided
      if (node1) {
        const nodeId = parseInt(node1, 10);
        if (!isNaN(nodeId)) {
          setSelectedIdMap1(nodeId);
          setShowPopupMap1(true);
        }
      }
      if (node2) {
        const nodeId = parseInt(node2, 10);
        if (!isNaN(nodeId)) {
          setSelectedIdMap2(nodeId);
          setShowPopupMap2(true);
        }
      }
    } else if (scenarioId1) {
      // Single scenario mode
      console.log('Single scenario mode');
      setSelectedScenario1(scenarioId1);

      // Apply node 1 if provided
      if (node1) {
        const nodeId = parseInt(node1, 10);
        if (!isNaN(nodeId)) {
          setSelectedIdMap1(nodeId);
          setShowPopupMap1(true);
        }
      }
    }

    setUrlParamsApplied(true);
  }, [dataLoaded, dataJson, urlParamsApplied]);

  // Helper function to calculate node metrics
  const calculateNodeMetrics = (nodeId, scenarioId) => {
    if (!nodeId || !scenarioId || !losDataJson.scenario) {
      return { flusso: 0, codaMedia: 0, ritardoMedio: 0 };
    }

    const selectedScenarioData = losDataJson.scenario.find(
      scenario => scenario.replicaAvg === scenarioId
    );

    if (selectedScenarioData?.LosNodi) {
      const selectedNode = selectedScenarioData.LosNodi.find(
        node => node.idNodo === nodeId
      );

      if (selectedNode) {
        let totalFlow = 0;
        let totalDelay = 0;
        let sectionCount = 0;

        Object.values(selectedNode.sezioni || {}).forEach(section => {
          totalFlow += section.count || 0;
          totalDelay += section.delaySec || 0;
          sectionCount++;
        });

        const avgDelay = sectionCount > 0 ? (totalDelay / sectionCount).toFixed(0) : 0;
        const nodeDelay = selectedNode.delayNode ? selectedNode.delayNode.toFixed(1) : 0;

        return {
          flusso: totalFlow,
          codaMedia: avgDelay,
          ritardoMedio: nodeDelay,
          nodeData: {
            ...selectedNode,
            totalVehicles: totalFlow,
            intersection: selectedNode.nome || 'Intersezione'
          }
        };
      }
    }

    return { flusso: 0, codaMedia: 0, ritardoMedio: 0 };
  };

  // Calculate metrics for map 1
  useEffect(() => {
    const metrics = calculateNodeMetrics(selectedIdMap1, selectedScenario1);
    setNodeMetricsMap1(metrics);
    if (metrics.nodeData && selectedIdMap1) {
      setNodeDataMap1(metrics.nodeData);
      // Set the node name for the indicator
      const nodeName = metrics.nodeData.nome || metrics.nodeData.intersection || 'Intersezione';
      setNodeNameMap1(nodeName);
    } else {
      setNodeDataMap1(null);
      setNodeNameMap1(null);
    }
  }, [selectedIdMap1, selectedScenario1, losDataJson]);

  // Calculate metrics for map 2
  useEffect(() => {
    const metrics = calculateNodeMetrics(selectedIdMap2, selectedScenario2);
    setNodeMetricsMap2(metrics);
    if (metrics.nodeData && selectedIdMap2) {
      setNodeDataMap2(metrics.nodeData);
      // Set the node name for the indicator
      const nodeName = metrics.nodeData.nome || metrics.nodeData.intersection || 'Intersezione';
      setNodeNameMap2(nodeName);
    } else {
      setNodeDataMap2(null);
      setNodeNameMap2(null);
    }
  }, [selectedIdMap2, selectedScenario2, losDataJson]);

  // Update road data when node data changes (for when road is selected first)
  useEffect(() => {
    if (selectedRoadDataMap1 && nodeDataMap1 && !selectedRoadDataMap1.sectionData) {
      const sections = Object.values(nodeDataMap1.sezioni || {});
      const selectedSection = sections[selectedRoadDataMap1.roadIndex];
      if (selectedSection) {
        setSelectedRoadDataMap1(prev => ({
          ...prev,
          sectionData: selectedSection
        }));
      }
    }
  }, [nodeDataMap1, selectedRoadDataMap1]);

  useEffect(() => {
    if (selectedRoadDataMap2 && nodeDataMap2 && !selectedRoadDataMap2.sectionData) {
      const sections = Object.values(nodeDataMap2.sezioni || {});
      const selectedSection = sections[selectedRoadDataMap2.roadIndex];
      if (selectedSection) {
        setSelectedRoadDataMap2(prev => ({
          ...prev,
          sectionData: selectedSection
        }));
      }
    }
  }, [nodeDataMap2, selectedRoadDataMap2]);

  const updateSelectedId = (id, mapIndex = 0) => {
    // Reset shared tooltip state when node changes
    if (showComparison) {
      setSharedTooltipState({
        selectedSectionId: null,
        showTooltip: false
      });
    }

    // Close Charts Popup when selecting a node (it's for road/section analysis)
    if (id !== null) {
      setShowChartsPopup(false);
    }

    // Update selected ID for the appropriate map
    if (mapIndex === 0) {
      setSelectedIdMap1(id);
      // Show or hide popup for map 1
      setShowPopupMap1(id !== null);
      // Clear road selection when node changes
      setSelectedRoadMap1(null);
      setSelectedRoadDataMap1(null);

      // If in comparison mode, sync the selection to the other map
      if (showComparison && id !== null) {
        setSelectedIdMap2(id);
        setShowPopupMap2(true);
        setSelectedRoadMap2(null);
        setSelectedRoadDataMap2(null);
      } else if (showComparison && id === null) {
        // Clear selection in the other map when deselecting
        setSelectedIdMap2(null);
        setShowPopupMap2(false);
        setSelectedRoadMap2(null);
        setSelectedRoadDataMap2(null);
      }
    } else {
      setSelectedIdMap2(id);
      // Show or hide popup for map 2
      setShowPopupMap2(id !== null);
      // Clear road selection when node changes
      setSelectedRoadMap2(null);
      setSelectedRoadDataMap2(null);

      // If in comparison mode, sync the selection to the other map
      if (showComparison && id !== null) {
        setSelectedIdMap1(id);
        setShowPopupMap1(true);
        setSelectedRoadMap1(null);
        setSelectedRoadDataMap1(null);
      } else if (showComparison && id === null) {
        // Clear selection in the other map when deselecting
        setSelectedIdMap1(null);
        setShowPopupMap1(false);
        setSelectedRoadMap1(null);
        setSelectedRoadDataMap1(null);
      }
    }
  };

  // Handler for road selection - closes node popup when road is selected
  // Helper to find road/section data in a specific scenario
  const findDataForRoadInScenario = (roadData, scenarioId) => {
    if (!losDataJson.scenario || !scenarioId) return { sectionData: null, nodeData: null };

    const selectedScenarioData = losDataJson.scenario.find(
      scenario => scenario.replicaAvg === scenarioId
    );

    if (selectedScenarioData?.LosNodi) {
      // If roadData has an osmId, find the node that contains this section
      if (roadData.osmId) {
        // Search all nodes for the one containing this OSM ID in its sections
        for (const node of selectedScenarioData.LosNodi) {
          const sectionKey = `${roadData.osmId}.0`;
          if (node.sezioni && node.sezioni[sectionKey]) {
            const sectionData = node.sezioni[sectionKey];

            // Calculate node metrics
            let totalFlow = 0;
            Object.values(node.sezioni || {}).forEach(section => {
              totalFlow += section.count || 0;
            });

            const nodeData = {
              ...node,
              totalVehicles: totalFlow,
              delayNode: node.delayNode,
              intersection: node.nome || 'Intersezione'
            };

            // Try to find display_name for the section in geometryOSM
            const osmItem = dataJson?.geometryOSM?.find(item => 
              String(item.id) === String(sectionKey) || 
              String(item.id) === String(roadData.osmId)
            );
            
            if (osmItem?.display_name && osmItem.display_name !== null) {
              sectionData.nomeSec = osmItem.display_name;
            } else if (osmItem?.name) {
              sectionData.nomeSec = osmItem.name;
            } else if (roadData.osmName) {
              sectionData.nomeSec = roadData.osmName;
            }

            return { sectionData, nodeData };
          }
        }
      } else if (roadData.nodeId) {
        // Fallback to old method if nodeId is provided (Aimsun internal roads)
        const selectedNode = selectedScenarioData.LosNodi.find(
          node => node.idNodo === roadData.nodeId
        );

        if (selectedNode && selectedNode.sezioni) {
          const sections = Object.values(selectedNode.sezioni);
          const sectionData = sections[roadData.roadIndex];

          let totalFlow = 0;
          Object.values(selectedNode.sezioni || {}).forEach(section => {
            totalFlow += section.count || 0;
          });

          const nodeData = {
            ...selectedNode,
            totalVehicles: totalFlow,
            delayNode: selectedNode.delayNode,
            intersection: selectedNode.nome || 'Intersezione'
          };

          return { sectionData, nodeData };
        }
      }
    }
    return { sectionData: null, nodeData: null };
  };

  const handleRoadSelection = (roadId, roadData, mapIndex) => {
    if (mapIndex === 0) {
      setSelectedRoadMap1(roadId);
      // Close node popup when selecting a road
      setShowPopupMap1(false);
      setSelectedIdMap1(null);

      const { sectionData, nodeData } = findDataForRoadInScenario(roadData, selectedScenario1);
      setSelectedRoadDataMap1({
        ...roadData,
        sectionData: sectionData,
        nodeData: nodeData
      });

      // Synchronize to map 2 if in comparison mode
      if (showComparison && selectedScenario2) {
        setSelectedRoadMap2(roadId);
        setShowPopupMap2(false);
        setSelectedIdMap2(null);

        const otherMapData = findDataForRoadInScenario(roadData, selectedScenario2);
        setSelectedRoadDataMap2({
          ...roadData,
          sectionData: otherMapData.sectionData,
          nodeData: otherMapData.nodeData
        });
      }
    } else {
      setSelectedRoadMap2(roadId);
      // Close node popup when selecting a road
      setShowPopupMap2(false);
      setSelectedIdMap2(null);

      const { sectionData, nodeData } = findDataForRoadInScenario(roadData, selectedScenario2);
      setSelectedRoadDataMap2({
        ...roadData,
        sectionData: sectionData,
        nodeData: nodeData
      });

      // Synchronize to map 1 if in comparison mode
      if (showComparison && selectedScenario1) {
        setSelectedRoadMap1(roadId);
        setShowPopupMap1(false);
        setSelectedIdMap1(null);

        const otherMapData = findDataForRoadInScenario(roadData, selectedScenario1);
        setSelectedRoadDataMap1({
          ...roadData,
          sectionData: otherMapData.sectionData,
          nodeData: otherMapData.nodeData
        });
      }
    }

    // Automatically open the "Analisi Sezione" (Charts) popup
    setShowChartsPopup(true);
  };

  const handleScenario1Select = (scenarioId) => {
    // Handle empty string or null selection
    if (!scenarioId || scenarioId === "") {
      setSelectedScenario1(null);
      setSelectedIdMap1(null);
      return;
    }
    const id = isNaN(scenarioId) ? scenarioId : Number(scenarioId);
    setSelectedScenario1(id);
  };

  const handleScenario2Select = (scenarioId) => {
    // Handle empty string or null selection
    if (!scenarioId || scenarioId === "") {
      setSelectedScenario2(null);
      setSelectedIdMap2(null);
      return;
    }
    const id = isNaN(scenarioId) ? scenarioId : Number(scenarioId);
    setSelectedScenario2(id);
  };

  const toggleComparisonMode = () => {
    setShowComparison(!showComparison);
    if (showComparison) {
      // Leaving comparison mode - clear second scenario
      setSelectedScenario2(null);
      setSelectedIdMap2(null);
    }
  };

  // Toggle download dropdown
  const handleDownloadClick = () => {
    setShowDownloadDropdown(!showDownloadDropdown);
  };

  // Download full scenario report
  const handleDownloadScenario = async () => {
    setShowDownloadDropdown(false);
    const hasScenario = showComparison ? (selectedScenario1 || selectedScenario2) : selectedScenario1;

    if (!hasScenario) {
      alert('Seleziona almeno uno scenario prima di scaricare il report.');
      return;
    }

    try {
      if (showComparison) {
        await generateNodeReportPDF(
          null, // No specific node - full scenario
          null,
          true,
          selectedScenario1,
          selectedScenario2,
          dataJson,
          nodeMetricsMap1,
          nodeMetricsMap2,
          losDataJson, // Pass losDataJson for full tables
          'scenario', // Report type
          null, // roadData1
          null, // roadData2
          null, // chartData1
          null, // chartData2
          graficiDataJson // Pass for chart data
        );
      } else {
        await generateNodeReportPDF(
          null,
          null,
          false,
          selectedScenario1,
          null,
          dataJson,
          nodeMetricsMap1,
          null,
          losDataJson,
          'scenario',
          null, // roadData1
          null, // roadData2
          null, // chartData1
          null, // chartData2
          graficiDataJson // Pass for chart data
        );
      }
    } catch (error) {
      console.error('Error generating scenario PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  // Download current selection report
  const handleDownloadSelection = async () => {
    setShowDownloadDropdown(false);
    const hasSelection = nodeDataMap1 || nodeDataMap2 || selectedRoadDataMap1 || selectedRoadDataMap2;

    if (!hasSelection) {
      return; // Button should be disabled anyway
    }

    try {
      if (showComparison) {
        await generateNodeReportPDF(
          nodeDataMap1,
          nodeDataMap2,
          true,
          selectedScenario1,
          selectedScenario2,
          dataJson,
          nodeMetricsMap1,
          nodeMetricsMap2,
          losDataJson,
          'selection',
          selectedRoadDataMap1,
          selectedRoadDataMap2,
          chartDataMap1,
          chartDataMap2,
          graficiDataJson // Pass for chart data
        );
      } else {
        await generateNodeReportPDF(
          nodeDataMap1,
          null,
          false,
          selectedScenario1,
          null,
          dataJson,
          nodeMetricsMap1,
          null,
          losDataJson,
          'selection',
          selectedRoadDataMap1,
          null,
          chartDataMap1,
          null,
          graficiDataJson // Pass for chart data
        );
      }
    } catch (error) {
      console.error('Error generating selection PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  // Check if selection download should be enabled
  const hasSelection = nodeDataMap1 || nodeDataMap2 || selectedRoadDataMap1 || selectedRoadDataMap2;
  const hasScenario = showComparison ? (selectedScenario1 || selectedScenario2) : selectedScenario1;

  // Handler for synchronized zoom changes between maps
  const handleSyncZoomChange = (zoom, sourceMapIndex) => {
    setSyncedZoom(zoom);
    setZoomSourceMap(sourceMapIndex);
  };

  const handleChartsClick = () => {
    // Close any open node popups when opening charts
    setShowPopupMap1(false);
    setShowPopupMap2(false);

    // Open the charts popup regardless of selection
    setShowChartsPopup(true);
    // Default to showing scenario 1 if available (check both node data and road data)
    const hasDataScenario1 = nodeDataMap1 || selectedRoadDataMap1?.nodeData;
    const hasDataScenario2 = nodeDataMap2 || selectedRoadDataMap2?.nodeData;
    setCurrentChartScenario(hasDataScenario1 ? 1 : (hasDataScenario2 ? 2 : 1));
  };

  const handleDeselectAll = () => {
    // Clear all selections
    setSelectedIdMap1(null);
    setSelectedIdMap2(null);
    setNodeDataMap1(null);
    setNodeDataMap2(null);
    setNodeNameMap1(null);
    setNodeNameMap2(null);
    setSelectedRoadMap1(null);
    setSelectedRoadMap2(null);
    setSelectedRoadDataMap1(null);
    setSelectedRoadDataMap2(null);
    setShowPopupMap1(false);
    setShowPopupMap2(false);
    // Reset charts popup
    setChartsPopupResetKey(prev => prev + 1);
  };

  // Handler for street highlighting from sunburst chart slice selection
  const handleStreetHighlight = (highlightData, mapIndex) => {
    if (highlightData && highlightData.osmId) {
      const roadId = `osm-${highlightData.osmId}`;
      if (mapIndex === 0) {
        setSelectedRoadMap1(roadId);
        // Sync to map 2 in comparison mode
        if (showComparison) {
          setSelectedRoadMap2(roadId);
        }
      } else {
        setSelectedRoadMap2(roadId);
        // Sync to map 1 in comparison mode
        if (showComparison) {
          setSelectedRoadMap1(roadId);
        }
      }
    } else {
      // Clear highlight
      if (mapIndex === 0) {
        setSelectedRoadMap1(null);
        if (showComparison) {
          setSelectedRoadMap2(null);
        }
      } else {
        setSelectedRoadMap2(null);
        if (showComparison) {
          setSelectedRoadMap1(null);
        }
      }
    }
  };

  // Handler for street highlighting from charts dropdown selection
  // This is a simpler handler that only highlights without closing popups
  const handleDropdownStreetHighlight = (roadId, roadData, mapIndex) => {
    if (roadId && roadData?.osmId) {
      const osmRoadId = `osm-${roadData.osmId}`;
      if (mapIndex === 0) {
        setSelectedRoadMap1(osmRoadId);
        if (showComparison) {
          setSelectedRoadMap2(osmRoadId);
        }
      } else {
        setSelectedRoadMap2(osmRoadId);
        if (showComparison) {
          setSelectedRoadMap1(osmRoadId);
        }
      }
    } else if (roadId) {
      // If no osmId but roadId is provided, use it directly
      if (mapIndex === 0) {
        setSelectedRoadMap1(roadId);
        if (showComparison) {
          setSelectedRoadMap2(roadId);
        }
      } else {
        setSelectedRoadMap2(roadId);
        if (showComparison) {
          setSelectedRoadMap1(roadId);
        }
      }
    }
  };

  // Color legend items
  const colorLegendItems = [
    { label: "Coda Media", color: SCORE_SCALE_WARN },
    { label: "Coda Max", color: SCORE_SCALE_WORST }
  ];

  // Prepare chart data for popup
  const prepareChartData = (nodeData) => {
    if (!nodeData || !nodeData.sezioni) return [];

    const sections = Object.values(nodeData.sezioni);
    const totalAmpiezza = sections.reduce((sum, sec) => sum + (sec.ampiezza || 0), 0);

    return sections.map((section, index) => {
      // Find OSM display name if possible
      const sectionId = section.idSec || section.nomeSec || `section-${index}`;
      const sectionKey = String(section.idSec).includes('.') ? String(section.idSec) : `${section.idSec}.0`;
      const osmItem = dataJson?.geometryOSM?.find(item => 
        String(item.id) === String(sectionKey) || 
        String(item.id) === String(sectionId) || 
        String(item.id) === String(section.idSec)
      );
      
      const displayName = (osmItem?.display_name && osmItem.display_name !== null) ? 
        osmItem.display_name : 
        (osmItem?.name || section.nomeSec || `Strada ${index + 1}`);

      return {
        name: section.losSec || 'N/A',
        value: totalAmpiezza > 0 ? Math.round((section.ampiezza / totalAmpiezza) * 100) : 0,
        color: losColor(section.losSec, section.color || '#999999'),
        // Include full section data for when slice is clicked
        sectionData: {
          ...section,
          nomeSec: displayName // Update nodeSec for tooltip display
        },
        // Add unique identifier based on section properties
        sectionId: sectionId,
        streetName: displayName
      };
    });
  };

  return (
    <div className="confronto-container">
      {/* Top Left Controls - Only show in single map mode */}
      {!showComparison && (
        <div className="confronto-controls-left">
          <ScenarioSelector
            scenarios={dataJson.scenario}
            selectedValue={selectedScenario1}
            onChange={handleScenario1Select}
            placeholder="Seleziona uno scenario"
            descriptions={scenarioDescriptions}
          />

          <ColorLegend
            items={colorLegendItems}
            visible={selectedIdMap1 !== null}
          />
        </div>
      )}

      {/* Top Right Controls */}
      <div className="confronto-controls-right">
        <ControlButton
          variant="icon"
          onClick={handleDeselectAll}
          title="Deselect All"
          round={true}
          className={`deselect-button ${(selectedIdMap1 || selectedIdMap2 || selectedRoadMap1 || selectedRoadMap2) ? 'active' : 'inactive'}`}
          icon={<XMarkIcon className="button-icon" />}
          style={{
            background: (selectedIdMap1 || selectedIdMap2 || selectedRoadMap1 || selectedRoadMap2) ? '#DC3545' : 'rgba(30, 30, 30, 0.9)',
            borderColor: (selectedIdMap1 || selectedIdMap2 || selectedRoadMap1 || selectedRoadMap2) ? 'transparent' : 'rgba(161, 227, 64, 0.3)'
          }}
        />

        <ControlButton
          variant="icon"
          onClick={handleChartsClick}
          title="Charts"
          icon={<ChartsIcon className="button-icon" />}
        />

        <div className="download-dropdown-container" ref={downloadDropdownRef}>
          <ControlButton
            variant="icon"
            onClick={handleDownloadClick}
            title="Download"
            icon={<DownloadIcon className="button-icon" />}
            className={showDownloadDropdown ? 'active' : ''}
          />
          {showDownloadDropdown && (
            <div className="download-dropdown-menu">
              <button
                className={`download-dropdown-item ${!hasScenario ? 'disabled' : ''}`}
                onClick={handleDownloadScenario}
                disabled={!hasScenario}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Scarica Scenario
              </button>
              <button
                className={`download-dropdown-item ${!hasSelection ? 'disabled' : ''}`}
                onClick={handleDownloadSelection}
                disabled={!hasSelection}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Scarica selezione corrente
              </button>
            </div>
          )}
        </div>

        <ControlButton
          variant="primary"
          active={showComparison}
          className={showComparison ? "exist-mode" : ""}
          onClick={toggleComparisonMode}
          icon={showComparison ? <FullscreenOutlined /> : <ConfrontoIcon className="button-icon" />}
        >
          {showComparison ? "Esci" : "Confronto"}
        </ControlButton>
      </div>

      {/* Map Container */}
      <div className="confronto-map-wrapper">
        {showComparison ? (
          <div className="confronto-comparison-mode">
            <div className="confronto-map-panel">
              {/* Scenario selector for first map */}
              <div className="map-scenario-selector">
                <ScenarioSelector
                  scenarios={dataJson.scenario}
                  selectedValue={selectedScenario1}
                  onChange={handleScenario1Select}
                  placeholder="Seleziona scenario 1"
                  disabledScenarios={selectedScenario2 ? [selectedScenario2] : []}
                  descriptions={scenarioDescriptions}
                />
              </div>

              {/* Color legend for map 1 - visible when node selected */}
              <div className="map-color-legend">
                <ColorLegend
                  items={colorLegendItems}
                  visible={selectedIdMap1 !== null}
                />
              </div>

              {/* Node or Road selection card for map 1 */}
              {(selectedIdMap1 && nodeNameMap1) && (
                <div className="map-node-indicator">
                  <div className="node-indicator-dot"></div>
                  <span>{nodeNameMap1}</span>
                </div>
              )}

              {/* No equivalent node message for map 1 */}
              {selectedIdMap1 && !nodeDataMap1 && selectedScenario1 && (
                <div className="map-no-equivalent-message">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>Nessun nodo equivalente in questo scenario</span>
                </div>
              )}

              {/* Road selection indicator for map 1 */}
              {selectedRoadDataMap1 && chartDataMap1 && (chartDataMap1.veicoli?.length || chartDataMap1.codaMax?.length || chartDataMap1.tempo?.length || chartDataMap1.velocita?.length) && (
                <div className="map-road-indicator">
                  <div className="road-indicator-line" style={{
                    backgroundColor: '#8C74FF'
                  }}></div>
                  <span>{selectedRoadDataMap1.sectionData?.nomeSec || selectedRoadDataMap1.osmName || 'Strada Selezionata'}</span>

                </div>
              )}

              {/* No data message when road is selected but no chart data - for map 1 */}
              {selectedRoadDataMap1 && (!chartDataMap1 || (!chartDataMap1.veicoli?.length && !chartDataMap1.codaMax?.length && !chartDataMap1.tempo?.length && !chartDataMap1.velocita?.length)) && (
                <div className="map-no-equivalent-message">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>La sezione selezionata non è disponibile in questo scenario</span>
                </div>
              )}



              {/* Always show map with overlay if no scenario */}
              <SimpleMap
                selectedIdScenario={selectedScenario1}
                selectedId={selectedIdMap1}
                onSelectNode={(id) => updateSelectedId(id, 0)}
                selectedRoadId={selectedRoadMap1}
                onSelectRoad={(roadId, roadData) => handleRoadSelection(roadId, roadData, 0)}
                fullscreen={true}
                showZoomControl={true}
                syncZoom={syncedZoom}
                onSyncZoomChange={handleSyncZoomChange}
                mapIndex={0}
                zoomSourceMap={zoomSourceMap}
              />

              {/* Show overlay when no scenario selected */}
              {!selectedScenario1 && (
                <div className="map-no-scenario-overlay">
                  <div className="no-scenario-message">
                    <span>Seleziona uno scenario per visualizzare i dati</span>
                  </div>
                </div>
              )}

            </div>

            <div className="confronto-map-panel">
              {/* Scenario selector for second map */}
              <div className="map-scenario-selector">
                <ScenarioSelector
                  scenarios={dataJson.scenario}
                  selectedValue={selectedScenario2}
                  onChange={handleScenario2Select}
                  placeholder="Seleziona scenario 2"
                  disabledScenarios={selectedScenario1 ? [selectedScenario1] : []}
                  descriptions={scenarioDescriptions}
                />
              </div>

              {/* Color legend for map 2 - visible when node selected */}
              <div className="map-color-legend">
                <ColorLegend
                  items={colorLegendItems}
                  visible={selectedIdMap2 !== null}
                />
              </div>

              {/* Node or Road selection card for map 2 */}
              {(selectedIdMap2 && nodeNameMap2) && (
                <div className="map-node-indicator">
                  <div className="node-indicator-dot"></div>
                  <span>{nodeNameMap2}</span>
                </div>
              )}

              {/* No equivalent node message for map 2 */}
              {selectedIdMap2 && !nodeDataMap2 && selectedScenario2 && (
                <div className="map-no-equivalent-message">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>Nessun nodo equivalente in questo scenario</span>
                </div>
              )}

              {/* Road selection indicator for map 2 */}
              {selectedRoadDataMap2 && chartDataMap2 && (chartDataMap2.veicoli?.length || chartDataMap2.codaMax?.length || chartDataMap2.tempo?.length || chartDataMap2.velocita?.length) && (
                <div className="map-road-indicator">
                  <div className="road-indicator-line" style={{
                    backgroundColor: '#8C74FF'
                  }}></div>
                  <span>{selectedRoadDataMap2.sectionData?.nomeSec || selectedRoadDataMap2.osmName || 'Strada Selezionata'}</span>

                </div>
              )}

              {/* No data message when road is selected but no chart data - for map 2 */}
              {selectedRoadDataMap2 && (!chartDataMap2 || (!chartDataMap2.veicoli?.length && !chartDataMap2.codaMax?.length && !chartDataMap2.tempo?.length && !chartDataMap2.velocita?.length)) && (
                <div className="map-no-equivalent-message">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>La sezione selezionata non è disponibile in questo scenario</span>
                </div>
              )}



              {/* Always show map with overlay if no scenario */}
              <SimpleMap
                selectedIdScenario={selectedScenario2}
                selectedId={selectedIdMap2}
                onSelectNode={(id) => updateSelectedId(id, 1)}
                selectedRoadId={selectedRoadMap2}
                onSelectRoad={(roadId, roadData) => handleRoadSelection(roadId, roadData, 1)}
                fullscreen={true}
                showZoomControl={true}
                syncZoom={syncedZoom}
                onSyncZoomChange={handleSyncZoomChange}
                mapIndex={1}
                zoomSourceMap={zoomSourceMap}
              />

              {/* Show overlay when no scenario selected */}
              {!selectedScenario2 && (
                <div className="map-no-scenario-overlay">
                  <div className="no-scenario-message">
                    <span>Seleziona uno scenario per visualizzare i dati</span>
                  </div>
                </div>
              )}

            </div>
          </div>
        ) : (
          <div className="confronto-map-panel">
            {/* Node or Road selection card for map 1 */}
            {(selectedIdMap1 && nodeNameMap1) && (
              <div className="map-node-indicator">
                <div className="node-indicator-dot"></div>
                <span>{nodeNameMap1}</span>
              </div>
            )}

            {/* Road selection indicator for single mode - show street name only when data exists */}
            {selectedRoadDataMap1 && chartDataMap1 && (chartDataMap1.veicoli?.length || chartDataMap1.codaMax?.length || chartDataMap1.tempo?.length || chartDataMap1.velocita?.length) && (
              <div className="map-road-indicator">
                <div className="road-indicator-line" style={{
                  backgroundColor: '#8C74FF'
                }}></div>
                <span>{selectedRoadDataMap1.sectionData?.nomeSec || selectedRoadDataMap1.osmName || 'Strada Selezionata'}</span>

              </div>
            )}

            {/* No data message when road is selected but no chart data - for single mode */}
            {selectedRoadDataMap1 && (!chartDataMap1 || (!chartDataMap1.veicoli?.length && !chartDataMap1.codaMax?.length && !chartDataMap1.tempo?.length && !chartDataMap1.velocita?.length)) && (
              <div className="map-no-equivalent-message">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>La sezione selezionata non è disponibile in questo scenario</span>
              </div>
            )}


            {/* Always show map in single mode */}
            <SimpleMap
              selectedIdScenario={selectedScenario1}
              selectedId={selectedIdMap1}
              onSelectNode={(id) => updateSelectedId(id, 0)}
              selectedRoadId={selectedRoadMap1}
              onSelectRoad={(roadId, roadData) => handleRoadSelection(roadId, roadData, 0)}
              fullscreen={true}
              zoomLevel={zoomLevel}
              onZoomChange={setZoomLevel}
              mapCenter={mapCenter}
              onCenterChange={setMapCenter}
            />

            {/* Show overlay when no scenario selected */}
            {!selectedScenario1 && (
              <div className="map-no-scenario-overlay">
                <div className="no-scenario-message">
                  <span>Seleziona uno scenario per visualizzare i dati</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Metrics Legend */}
      {!showComparison ? (
        /* Single map mode - one legend at bottom left */
        <MetricsLegend
          flusso={getSistemaMetrics(selectedScenario1).flusso}
          codaMedia={getSistemaMetrics(selectedScenario1).codaMedia}
          ritardoMedio={getSistemaMetrics(selectedScenario1).ritardoMedio}
          visible={selectedScenario1 !== null}
        />
      ) : (
        /* Comparison mode - show legends for both maps */
        <>
          <MetricsLegend
            flusso={getSistemaMetrics(selectedScenario1).flusso}
            codaMedia={getSistemaMetrics(selectedScenario1).codaMedia}
            ritardoMedio={getSistemaMetrics(selectedScenario1).ritardoMedio}
            visible={selectedScenario1 !== null}
            position="left"
          />
          <MetricsLegend
            flusso={getSistemaMetrics(selectedScenario2).flusso}
            codaMedia={getSistemaMetrics(selectedScenario2).codaMedia}
            ritardoMedio={getSistemaMetrics(selectedScenario2).ritardoMedio}
            visible={selectedScenario2 !== null}
            position="right"
          />
        </>
      )}

      {/* Node Details Popups - One per map */}
      {showComparison ? (
        <>
          {/* Popup for Map 1 */}
          {showPopupMap1 && nodeDataMap1 && (
            <div className="popup-wrapper popup-map-1">
              <NodeDetailsPopup
                isOpen={showPopupMap1}
                onClose={() => {
                  setShowPopupMap1(false);
                  setSelectedIdMap1(null);
                  // Reset ChartsPopup state
                  setChartsPopupResetKey(prev => prev + 1);
                  // Reset shared tooltip state
                  setSharedTooltipState({
                    selectedSectionId: null,
                    showTooltip: false
                  });
                  // Also close and deselect in the other map in comparison mode
                  if (showComparison) {
                    setShowPopupMap2(false);
                    setSelectedIdMap2(null);
                  }
                }}
                nodeData={nodeDataMap1}
                sectionData={prepareChartData(nodeDataMap1)}
                scenarioNumber={1}
                // Pass shared tooltip state for synchronization
                sharedTooltipState={sharedTooltipState}
                onTooltipChange={setSharedTooltipState}
                isInComparisonMode={showComparison}
                onStreetHighlight={(data) => handleStreetHighlight(data, 0)}
              />
            </div>
          )}

          {/* Popup for Map 2 */}
          {showPopupMap2 && nodeDataMap2 && (
            <div className="popup-wrapper popup-map-2">
              <NodeDetailsPopup
                isOpen={showPopupMap2}
                onClose={() => {
                  setShowPopupMap2(false);
                  setSelectedIdMap2(null);
                  // Reset ChartsPopup state
                  setChartsPopupResetKey(prev => prev + 1);
                  // Reset shared tooltip state
                  setSharedTooltipState({
                    selectedSectionId: null,
                    showTooltip: false
                  });
                  // Also close and deselect in the other map in comparison mode
                  if (showComparison) {
                    setShowPopupMap1(false);
                    setSelectedIdMap1(null);
                  }
                }}
                nodeData={nodeDataMap2}
                sectionData={prepareChartData(nodeDataMap2)}
                scenarioNumber={2}
                // Pass shared tooltip state for synchronization
                sharedTooltipState={sharedTooltipState}
                onTooltipChange={setSharedTooltipState}
                isInComparisonMode={showComparison}
                onStreetHighlight={(data) => handleStreetHighlight(data, 1)}
              />
            </div>
          )}
        </>
      ) : (
        /* Single popup for single map mode */
        showPopupMap1 && nodeDataMap1 && (
          <div className="popup-wrapper popup-single">
            <NodeDetailsPopup
              isOpen={showPopupMap1}
              onClose={() => {
                setShowPopupMap1(false);
                setSelectedIdMap1(null);
                // Reset ChartsPopup state
                setChartsPopupResetKey(prev => prev + 1);
              }}
              nodeData={nodeDataMap1}
              sectionData={prepareChartData(nodeDataMap1)}
              scenarioNumber={1}
              // No shared state needed in single mode
              isInComparisonMode={false}
              onStreetHighlight={(data) => handleStreetHighlight(data, 0)}
            />
          </div>
        )
      )}

      {/* Charts Popup */}
      {/* Charts Popup - Split in comparison mode */}
      {showComparison ? (
        <>
          <ChartsPopup
            key={`chart-1-${chartsPopupResetKey}`}
            isOpen={showChartsPopup}
            onClose={() => {
              setShowChartsPopup(false);
              // Auto-deselect road when closing Analisi Sezione (per PDF requirement)
              setSelectedRoadMap1(null);
              setSelectedRoadMap2(null);
              setSelectedRoadDataMap1(null);
              setSelectedRoadDataMap2(null);
              // Clear chart data so reopening shows empty state
              setChartDataMap1(null);
              setChartDataMap2(null);
            }}
            nodeData1={nodeDataMap1}
            nodeData2={null}
            roadData1={selectedRoadDataMap1}
            roadData2={null}
            currentScenario={1}
            onScenarioChange={() => { }}
            showComparison={false}
            selectedScenario1={selectedScenario1}
            selectedScenario2={null}
            losDataJson={losDataJson}
            alignment="left"
            syncScrollTop={chartsScrollTop}
            onSyncScroll={setChartsScrollTop}
            onRoadSelect={(roadId, roadData) => handleDropdownStreetHighlight(roadId, roadData, 0)}
            sharedExpandedChart={sharedExpandedChart}
            onExpandedChartChange={setSharedExpandedChart}
            onChartDataReady={setChartDataMap1}
            sharedTimeInterval={sharedTimeInterval}
            onTimeIntervalChange={setSharedTimeInterval}
            sharedSelectedRoad={sharedSelectedRoad}
            onSelectedRoadChange={setSharedSelectedRoad}
          />
          <ChartsPopup
            key={`chart-2-${chartsPopupResetKey}`}
            isOpen={showChartsPopup}
            onClose={() => {
              setShowChartsPopup(false);
              // Auto-deselect road when closing Analisi Sezione (per PDF requirement)
              setSelectedRoadMap1(null);
              setSelectedRoadMap2(null);
              setSelectedRoadDataMap1(null);
              setSelectedRoadDataMap2(null);
              // Clear chart data so reopening shows empty state
              setChartDataMap1(null);
              setChartDataMap2(null);
            }}
            nodeData1={null}
            nodeData2={nodeDataMap2}
            roadData1={null}
            roadData2={selectedRoadDataMap2}
            currentScenario={2}
            onScenarioChange={() => { }}
            showComparison={false}
            selectedScenario1={null}
            selectedScenario2={selectedScenario2}
            losDataJson={losDataJson}
            alignment="right"
            syncScrollTop={chartsScrollTop}
            onSyncScroll={setChartsScrollTop}
            onRoadSelect={(roadId, roadData) => handleDropdownStreetHighlight(roadId, roadData, 1)}
            sharedExpandedChart={sharedExpandedChart}
            onExpandedChartChange={setSharedExpandedChart}
            onChartDataReady={setChartDataMap2}
            sharedTimeInterval={sharedTimeInterval}
            onTimeIntervalChange={setSharedTimeInterval}
            sharedSelectedRoad={sharedSelectedRoad}
            onSelectedRoadChange={setSharedSelectedRoad}
          />
        </>
      ) : (
        <ChartsPopup
          key={chartsPopupResetKey}
          isOpen={showChartsPopup}
          onClose={() => {
            setShowChartsPopup(false);
            // Auto-deselect road when closing Analisi Sezione (per PDF requirement)
            setSelectedRoadMap1(null);
            setSelectedRoadDataMap1(null);
            // Clear chart data so reopening shows empty state
            setChartDataMap1(null);
          }}
          nodeData1={nodeDataMap1}
          nodeData2={nodeDataMap2}
          roadData1={selectedRoadDataMap1}
          roadData2={selectedRoadDataMap2}
          currentScenario={currentChartScenario}
          onScenarioChange={setCurrentChartScenario}
          showComparison={showComparison}
          selectedScenario1={selectedScenario1}
          selectedScenario2={selectedScenario2}
          losDataJson={losDataJson}
          alignment="right"
          onRoadSelect={(roadId, roadData) => handleDropdownStreetHighlight(roadId, roadData, 0)}
          onExpandedChartChange={setSingleExpandedChart}
          onChartDataReady={setChartDataMap1}
        />
      )}

      {/* Dual Expanded Chart Modals for Comparison Mode */}
      {sharedExpandedChart && showComparison && (
        <div className="expanded-chart-dual-overlay" onClick={() => setSharedExpandedChart(null)}>
          <div className="expanded-chart-dual-container" onClick={(e) => e.stopPropagation()}>
            <div className="expanded-chart-dual-header">
              <h2>{sharedExpandedChart.title}</h2>
              <div className="expanded-chart-dual-controls">
                <select
                  value={sharedTimeInterval}
                  onChange={(e) => setSharedTimeInterval(e.target.value)}
                  className="expanded-time-selector"
                >
                  <option value="10min">10 min</option>
                  <option value="30min">30 min</option>
                  <option value="1h">1 ora</option>
                </select>
                <button className="expanded-chart-close" onClick={() => setSharedExpandedChart(null)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="expanded-chart-dual-content">
              {/* Scenario 1 Chart */}
              <div className="expanded-chart-panel">
                <div className="expanded-chart-panel-title">Scenario 1</div>
                <div className="expanded-chart-panel-content">
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart
                      data={chartDataMap1 ? (
                        sharedExpandedChart.dataKey === 'veicoli' ? chartDataMap1.veicoli :
                          sharedExpandedChart.dataKey === 'codaMax' ? chartDataMap1.codaMax :
                            sharedExpandedChart.dataKey === 'tempo' ? chartDataMap1.tempo :
                              chartDataMap1.velocita
                      ) : []}
                      margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                      <defs>
                        <linearGradient id={`expanded-${sharedExpandedChart.dataKey}-1`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={sharedExpandedChart.color} stopOpacity={0.8} />
                          <stop offset="95%" stopColor={sharedExpandedChart.color} stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#e5e5e5" />
                      <XAxis dataKey="time" stroke="#666" tick={{ fontSize: 12 }} axisLine={{ stroke: '#e0e0e0' }} />
                      <YAxis stroke="#666" tick={{ fontSize: 12 }} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }} />
                      <Area
                        type="stepAfter"
                        dataKey="value"
                        stroke={sharedExpandedChart.color}
                        strokeWidth={3}
                        fillOpacity={1}
                        fill={`url(#expanded-${sharedExpandedChart.dataKey}-1)`}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {/* Scenario 2 Chart */}
              <div className="expanded-chart-panel">
                <div className="expanded-chart-panel-title">Scenario 2</div>
                <div className="expanded-chart-panel-content">
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart
                      data={chartDataMap2 ? (
                        sharedExpandedChart.dataKey === 'veicoli' ? chartDataMap2.veicoli :
                          sharedExpandedChart.dataKey === 'codaMax' ? chartDataMap2.codaMax :
                            sharedExpandedChart.dataKey === 'tempo' ? chartDataMap2.tempo :
                              chartDataMap2.velocita
                      ) : []}
                      margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                      <defs>
                        <linearGradient id={`expanded-${sharedExpandedChart.dataKey}-2`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={sharedExpandedChart.color} stopOpacity={0.8} />
                          <stop offset="95%" stopColor={sharedExpandedChart.color} stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#e5e5e5" />
                      <XAxis dataKey="time" stroke="#666" tick={{ fontSize: 12 }} axisLine={{ stroke: '#e0e0e0' }} />
                      <YAxis stroke="#666" tick={{ fontSize: 12 }} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }} />
                      <Area
                        type="stepAfter"
                        dataKey="value"
                        stroke={sharedExpandedChart.color}
                        strokeWidth={3}
                        fillOpacity={1}
                        fill={`url(#expanded-${sharedExpandedChart.dataKey}-2)`}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single Expanded Chart Modal for Single Mode */}
      {singleExpandedChart && !showComparison && (
        <ExpandedChartModal
          isOpen={true}
          onClose={() => setSingleExpandedChart(null)}
          chartData={chartDataMap1 ? (
            singleExpandedChart.dataKey === 'veicoli' ? chartDataMap1.veicoli :
              singleExpandedChart.dataKey === 'codaMax' ? chartDataMap1.codaMax :
                singleExpandedChart.dataKey === 'tempo' ? chartDataMap1.tempo :
                  chartDataMap1.velocita
          ) : []}
          chartConfig={{
            color: singleExpandedChart.color,
            gradientId: singleExpandedChart.dataKey
          }}
          title={singleExpandedChart.title}
          footer={null}
          showTimeSelector={false}
        />
      )}
    </div>
  );
}

export default Confronto;